import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import type { RosterLock } from "../../../common/types.ts";

// Safely read the contest roster lock. Undefined for normal (non-contest)
// leagues. g.get throws for unset keys and rosterLock has no default, so guard
// with hasOwn.
export const getRosterLock = (): RosterLock | undefined =>
	Object.hasOwn(g, "rosterLock") ? g.get("rosterLock") : undefined;

// Whether a player is locked to their contest team and therefore may not be
// traded away, released, or cut. Fail-closed enforcement lives at the roster
// mutation chokepoints (trade/isUntradable, player/release).
export const isPidLocked = (pid: number): boolean => {
	const lock = getRosterLock();
	return lock !== undefined && lock.lockedPids.includes(pid);
};

// Decide which players on a contest roster get locked: the highest-value ones,
// leaving `maxFreeSlots` slots for the user to change. Pure and deterministic
// (total-order tiebreak on pid) so every entrant gets the identical lock.
// Called once at league creation to resolve the campaign's RosterLockSpec.
export const resolveLockedPids = (
	players: { pid: number; value: number }[],
	maxFreeSlots: number,
): number[] => {
	const sorted = [...players].sort((a, b) => b.value - a.value || a.pid - b.pid);
	const numLocked = Math.max(0, sorted.length - maxFreeSlots);
	return sorted
		.slice(0, numLocked)
		.map((p) => p.pid)
		.sort((a, b) => a - b);
};

// Enforce the free-slot cap when ADDING a non-locked player to the contest team
// (free-agent signing, draft pick). Throws if the team already uses all its
// free slots. No-op for normal leagues and for any team that isn't the contest
// team. Locked players are always allowed (they're part of the fixed roster).
export const assertFreeSlotAvailable = async (
	tid: number,
	pid: number,
): Promise<void> => {
	const lock = getRosterLock();
	if (lock === undefined || tid !== lock.tid || lock.lockedPids.includes(pid)) {
		return;
	}

	const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
	// Count non-locked players already on the team, excluding this player (so
	// re-signing the existing free-slot player doesn't count against the cap).
	const freeUsed = roster.filter(
		(p) => p.pid !== pid && !lock.lockedPids.includes(p.pid),
	).length;

	if (freeUsed >= lock.maxFreeSlots) {
		throw new Error(
			`This challenge only lets you change ${lock.maxFreeSlots} roster spot${
				lock.maxFreeSlots === 1 ? "" : "s"
			}`,
		);
	}
};
